import { useState, useEffect } from 'react'
import { ColorDot } from '@/components/ui/color-dot'
import { PriorityBadge } from '@/components/ui/priority-badge'
import type { TaskResponse } from '@/types/task'
import type { PipelineStatusResponse } from '@/types/pipeline'

type KanbanColumn =
  | { type: 'status'; status: PipelineStatusResponse }
  | { type: 'none' }

interface TaskKanbanViewProps {
  tasks: TaskResponse[]
  statuses: PipelineStatusResponse[]
  onTaskClick: (id: string) => void
  onStatusChange?: (taskId: string, newStatusId: string | null) => void
}

export function TaskKanbanView({ tasks, statuses, onTaskClick, onStatusChange }: TaskKanbanViewProps) {
  const sortedStatuses = [...statuses].sort((a, b) => a.position - b.position)

  const columns: KanbanColumn[] = [
    { type: 'none' },
    ...sortedStatuses.map((s): KanbanColumn => ({ type: 'status', status: s })),
  ]

  const [draggingId, setDraggingId]       = useState<string | null>(null)
  const [overColumnKey, setOverColumnKey] = useState<string | null>(null)
  // Optimistic overrides: task.id → new status_id (null = tanpa status)
  const [pendingMoves, setPendingMoves]   = useState<Record<string, string | null>>({})

  // Clear pending moves setelah data segar dari server masuk
  useEffect(() => { setPendingMoves({}) }, [tasks])

  function effectiveStatusId(task: TaskResponse): string | null {
    return task.id in pendingMoves ? pendingMoves[task.id] : task.status_id
  }

  function tasksForColumn(col: KanbanColumn): TaskResponse[] {
    if (col.type === 'none') return tasks.filter((t) => effectiveStatusId(t) === null)
    return tasks.filter((t) => effectiveStatusId(t) === col.status.id)
  }

  function handleDrop(e: React.DragEvent, col: KanbanColumn) {
    e.preventDefault()
    const taskId = e.dataTransfer.getData('text/plain')
    if (!taskId) return

    const newStatusId = col.type === 'none' ? null : col.status.id

    // Optimistic update lokal
    setPendingMoves((prev) => ({ ...prev, [taskId]: newStatusId }))
    onStatusChange?.(taskId, newStatusId)

    setOverColumnKey(null)
    setDraggingId(null)
  }

  const isDragActive = draggingId !== null

  return (
    <div className="flex gap-3 overflow-x-auto pb-2">
      {columns.map((col) => {
        const colTasks = tasksForColumn(col)
        const colKey   = col.type === 'none' ? '__none__' : col.status.id
        const colName  = col.type === 'none' ? 'Tanpa Status' : col.status.name
        const colColor = col.type === 'none' ? '#6b7280' : col.status.color
        const isOver   = overColumnKey === colKey

        return (
          <div
            key={colKey}
            onDragOver={(e) => { e.preventDefault(); setOverColumnKey(colKey) }}
            onDragLeave={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                setOverColumnKey(null)
              }
            }}
            onDrop={(e) => handleDrop(e, col)}
            className={`flex w-64 shrink-0 flex-col rounded-xl border transition-colors duration-150 ${
              isOver
                ? 'border-primary bg-primary/5'
                : isDragActive
                  ? 'border-border/60 bg-muted/20'
                  : 'border-border bg-muted/30'
            }`}
          >
            {/* Column header */}
            <div className="flex items-center gap-2 border-b border-border px-3 py-2.5">
              <ColorDot color={colColor} size="md" />
              <span className="flex-1 truncate text-sm font-medium">{colName}</span>
              <span className="shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-xs font-medium text-muted-foreground">
                {colTasks.length}
              </span>
            </div>

            {/* Task cards — independently scrollable */}
            <div
              className="flex flex-col gap-2 overflow-y-auto p-2"
              style={{ minHeight: '3rem', maxHeight: '60vh' }}
            >
              {colTasks.length === 0 ? (
                <p className={`py-4 text-center text-xs transition-colors ${
                  isOver ? 'text-primary' : 'text-muted-foreground'
                }`}>
                  {isOver ? 'Lepaskan di sini' : 'Kosong'}
                </p>
              ) : (
                colTasks.map((task) => {
                  const isDragging = draggingId === task.id
                  const isPending  = task.id in pendingMoves
                  return (
                    <button
                      key={task.id}
                      type="button"
                      draggable
                      onDragStart={(e) => {
                        setDraggingId(task.id)
                        e.dataTransfer.setData('text/plain', task.id)
                        e.dataTransfer.effectAllowed = 'move'
                      }}
                      onDragEnd={() => setDraggingId(null)}
                      onClick={() => !isDragging && onTaskClick(task.id)}
                      className={`w-full rounded-md border border-border bg-card px-3 py-2 text-left transition-all select-none ${
                        isDragging
                          ? 'opacity-40 cursor-grabbing shadow-none'
                          : isPending
                            ? 'opacity-70 cursor-grab'
                            : 'cursor-grab hover:bg-secondary'
                      }`}
                    >
                      <p className="text-xs font-medium leading-snug">{task.title}</p>
                      <div className="mt-1.5">
                        <PriorityBadge priority={task.priority} />
                      </div>
                    </button>
                  )
                })
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
